import { MSelect } from '@Components/Base';
import { Col, Form, Row } from 'antd';
import { FormikProps } from 'formik';
import { Option } from '@Components/types/options';
import { useEffect, useState, useMemo } from 'react';

interface SearchableSelectProps<T> {
  formik: FormikProps<T>;
  options: Option[];
  formikKey: string;
  label?: string;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  showSearch?: boolean;
  filterOption?: boolean;
}

const SearchableSelect = <T,>({
  formik,
  options,
  formikKey,
  label,
  placeholder,
  loading,
  disabled,
  showSearch = true,
  filterOption = true,
}: SearchableSelectProps<T>): JSX.Element => {
  const { value } = formik.getFieldMeta(formikKey);
  const [searchValue, setSearchValue] = useState<string>('');

  // Filter options based on search value
  const filteredOptions = useMemo(() => {
    if (!searchValue || !filterOption) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue, filterOption]);

  // Clear value when there are no options
  useEffect(() => {
    if (options.length === 0 && value) {
      formik.setFieldValue(formikKey, '');
    }
  }, [options, value, formikKey, formik]);

  return (
    <Row>
      <Col span={24}>
        <Form.Item>
          <MSelect
            labeloption={label}
            options={filteredOptions}
            value={options.length === 0 ? undefined : value}
            onChange={(selectedValue) => {
              formik.setFieldValue(formikKey, selectedValue);
            }}
            onSearch={showSearch ? setSearchValue : undefined}
            placeholder={placeholder}
            loading={loading}
            disabled={disabled}
            showSearch={showSearch}
            filterOption={false} // We handle filtering manually
            notFoundContent={
              searchValue && filteredOptions.length === 0
                ? 'No results found'
                : undefined
            }
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default SearchableSelect;
